import type { ModelParam, ModelRevision, ModelValues } from "../api/types";
import { creditsWord } from "../copy/errors";
import { Button, Pill } from "../ui";
import { tunedText } from "./format";

export const WAIT_TITLE = "Wait for the current step";

/** One row of the history: a step, or a run of tuning steps read as one. */
interface Row {
  /** The step the row stands for, and restores to: the newest of a run. */
  step: ModelRevision;
  text: string;
  /** A second line: a refusal's reason. */
  note?: string;
  credits?: string;
  picture: boolean;
}

const ok = (step: ModelRevision) => step.status === "ok";

/** A step of a sculpted design that only changed its size: no generation
 *  ([sculpted §2.4]). */
export function isResize(step: Pick<ModelRevision, "source" | "values">) {
  return step.source === "mesh" && step.values?.resize_of !== undefined;
}

/** "30", "45.5": metres as centimetres. */
function cm(metres: unknown): string {
  return typeof metres === "number"
    ? String(Math.round(metres * 1e4) / 100)
    : "";
}
const tuned = (step: ModelRevision) => ok(step) && step.source === "params";

/** "Stopped · 50 credits", "Stopped · not charged". */
function stopped(credits: number): string {
  return `Stopped · ${credits > 0 ? creditsWord(credits) : "not charged"}`;
}

/** The rows of a history, newest first. Consecutive tuning steps are one row
 *  that lists the net changes from before the first to after the last. */
export function historyRows(
  revisions: ModelRevision[],
  params: ModelParam[],
): Row[] {
  const valuesOf = (n: number | null): ModelValues | null =>
    revisions.find((step) => step.n === n)?.values ?? null;
  const rows: Row[] = [];
  for (let i = 0; i < revisions.length; i += 1) {
    const step = revisions[i];
    if (tuned(step)) {
      // The run: this step and every tuning step directly before it.
      let oldest = step;
      while (i + 1 < revisions.length && tuned(revisions[i + 1])) {
        i += 1;
        oldest = revisions[i];
      }
      const changes = tunedText(valuesOf(oldest.parent_n), step.values, params);
      rows.push({
        step,
        text: changes ? `Tuned: ${changes}` : "Tuned",
        picture: false,
      });
    } else if (step.status === "queued" || step.status === "running") {
      rows.push({ step, text: "Working…", picture: false });
    } else if (step.status === "failed") {
      rows.push({ step, text: "Did not work · not charged", picture: false });
    } else if (step.status === "refused") {
      rows.push({
        step,
        text: "Could not be done · not charged",
        note: step.refusal_reason ?? undefined,
        picture: false,
      });
    } else if (step.status === "cancelled") {
      rows.push({ step, text: stopped(step.credits), picture: false });
    } else if (step.source === "restore") {
      rows.push({
        step,
        text: `Restored step ${step.parent_n ?? ""}`.trim(),
        picture: false,
      });
    } else if (isResize(step)) {
      const from = cm(valuesOf(Number(step.values?.resize_of))?.longest_m);
      const to = cm(step.values?.longest_m);
      rows.push({
        step,
        text: from && to ? `Resized: ${from} → ${to} cm` : "Resized",
        picture: true,
      });
    } else if (step.source === "remix") {
      rows.push({
        step,
        text: `Started from ${step.instruction ?? ""}`.trim(),
        picture: false,
      });
    } else {
      rows.push({
        step,
        text: `${step.source === "mesh" ? "Generated" : "You"}: ${step.instruction ?? ""}`,
        credits: creditsWord(step.credits),
        picture: true,
      });
    }
  }
  return rows;
}

/** The steps of a design, newest first ([design-studio §11.8]). The current
 *  one is marked; every other step that succeeded can be restored, without a
 *  question. A step that did not succeed has no picture. */
export function HistoryPanel(props: {
  revisions: ModelRevision[];
  currentN: number | null;
  /** The current step's settings: the labels and units of a tuning row. */
  params?: ModelParam[];
  /** While a step runs nothing can be restored. */
  disabled: boolean;
  onRestore(n: number): void;
}): JSX.Element {
  const rows = historyRows(props.revisions, props.params ?? []);
  return (
    <section className="flex flex-col gap-2" data-testid="history-panel">
      <h2 className="tb-h-row m-0">History</h2>
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {rows.map((row) => {
          const { step } = row;
          const current = step.n === props.currentN;
          return (
            <li
              key={step.n}
              data-testid="history-row"
              aria-current={current ? "true" : undefined}
              className={[
                "flex flex-col gap-2 rounded-lg border p-3 text-sm",
                current
                  ? "border-brand bg-brand-tint/40"
                  : "border-slate-200 bg-white",
              ].join(" ")}
            >
              {row.picture && step.view_url ? (
                <img
                  src={step.view_url}
                  alt=""
                  loading="lazy"
                  className="block aspect-square w-full max-w-[160px] rounded-md bg-slate-100 object-cover"
                />
              ) : null}
              <p className="m-0 break-words text-slate-800">{row.text}</p>
              {row.note ? (
                <p className="m-0 break-words text-slate-600">{row.note}</p>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-slate-500">
                  {row.credits ?? ""}
                </span>
                {current ? (
                  <Pill size="sm" tone="purple">
                    Current
                  </Pill>
                ) : ok(step) ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
                    disabled={props.disabled}
                    title={props.disabled ? WAIT_TITLE : undefined}
                    onClick={() => props.onRestore(step.n)}
                  >
                    Restore
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
